/**
 * Cliente HTTP de auth — wrapper sobre `fetch` para los endpoints de
 * `/api/auth/*`.
 *
 * Todas las llamadas:
 *  - Usan `credentials: 'include'` para que el browser mande/reciba la
 *    cookie de sesión.
 *  - Pegan a paths relativos (`/api/auth/...`) — Vite proxa al backend
 *    en dev (`web/vite.config.ts`); en prod nginx hace el reverse proxy.
 *  - Devuelven una unión `{ok: true, data} | {ok: false, error, code,
 *    status}` para que el caller la trate sin try/catch.
 */

import type { AuthenticatedPilot } from '@51esc/shared';

interface ApiErrorBody {
  error: string;
  code: string;
  details?: unknown;
}

export type AuthResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; code: string; status: number };

interface MeResponse {
  user: AuthenticatedPilot;
}

interface RegisterPayload {
  callsign: string;
  email: string;
  password: string;
  cfTurnstileResponse: string;
}

interface LoginPayload {
  callsign: string;
  password: string;
}

interface VerifyEmailPayload {
  token: string;
}

/** Body de los endpoints que solo reciben un email (reenvío / reset). */
interface EmailOnlyPayload {
  email: string;
}

interface PasswordResetConfirmPayload {
  token: string;
  newPassword: string;
}

async function request<T>(
  path: string,
  init: RequestInit,
): Promise<AuthResult<T>> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(init.headers ?? {}),
      },
    });
  } catch (err) {
    console.error('[auth-api] fallo de red:', err);
    return {
      ok: false,
      error: 'No se ha podido contactar con el servidor.',
      code: 'network_error',
      status: 0,
    };
  }

  // 204 No Content — logout u operaciones que no devuelven body.
  if (res.status === 204) {
    return { ok: true, data: undefined as T };
  }

  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* ignore — algunos errores no devuelven JSON */
  }

  if (!res.ok) {
    const e = (body ?? {}) as Partial<ApiErrorBody>;
    return {
      ok: false,
      error: e.error ?? `Error ${res.status}`,
      code: e.code ?? 'unknown_error',
      status: res.status,
    };
  }

  return { ok: true, data: body as T };
}

/** `POST /api/auth/register` */
export function register(payload: RegisterPayload) {
  return request<{ ok: true }>('/api/auth/register', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** `POST /api/auth/login` */
export function login(payload: LoginPayload) {
  return request<MeResponse>('/api/auth/login', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** `POST /api/auth/logout` */
export function logout() {
  return request<undefined>('/api/auth/logout', { method: 'POST' });
}

/** `GET /api/auth/me` — útil para detectar sesión existente. */
export function me() {
  return request<MeResponse>('/api/auth/me', { method: 'GET' });
}

/** `POST /api/auth/verify-email` — consume el token de verificación. */
export function verifyEmail(payload: VerifyEmailPayload) {
  return request<{ ok: true }>('/api/auth/verify-email', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** `POST /api/auth/resend-verification` — reenvía el email de verificación. */
export function resendVerification(payload: EmailOnlyPayload) {
  return request<{ ok: true }>('/api/auth/resend-verification', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** `POST /api/auth/password-reset/request` — pide el email de reset. */
export function requestPasswordReset(payload: EmailOnlyPayload) {
  return request<{ ok: true }>('/api/auth/password-reset/request', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/** `POST /api/auth/password-reset/confirm` — fija la nueva contraseña. */
export function confirmPasswordReset(payload: PasswordResetConfirmPayload) {
  return request<{ ok: true }>('/api/auth/password-reset/confirm', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}
